import { useEffect, useMemo, useRef, useState } from 'react'
import { AmountInput, Button, DatePicker, Drawer, SearchSelect } from '../components'
import { useMe } from '../lib/use-me'
import { useAccounts } from '../lib/api/accounts'
import { useLatestFxRate } from '../lib/api/fx-rates'
import { SALARY_CATEGORY_ID, useCategories } from '../lib/api/categories'
import { useCounterparties } from '../lib/api/counterparties'
import { useEmployees } from '../lib/api/employees'
import { useOrganizations } from '../lib/api/organizations'
import { useProjects } from '../lib/api/projects'
import { useAddSalaryPayment, useSalaryEntries } from '../lib/api/salaries'
import { useTags } from '../lib/api/tags'
import {
  useCreateTransaction,
  useUpdateTransaction,
  type CreateTransactionInput,
} from '../lib/api/transactions'
import { fmtMoney } from '../lib/money'
import { accountFxQuote, amountInAccountCurrency } from '../lib/account-fx'
import type { Currency, Transaction, TransactionType } from '../lib/api/types'

// Discriminant on `mode` — the parent always knows whether it's creating a new
// row or editing an existing one. Defaults are advisory (only consulted in
// 'new' mode): they pre-fill account/org from the calling page's context
// (e.g. the rail-selected account on /transactions).
export type DrawerTx =
  | { mode: 'new'; defaults?: TxDefaults }
  | { mode: 'edit'; transaction: Transaction }

export type TxDefaults = {
  accountId?: string | null
  organizationId?: string | null
}

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 4,
  padding: '6px 8px',
  background: 'var(--bg-1)',
  color: 'var(--fg-1)',
  fontSize: 13,
}

const round2 = (n: number) => Math.round(n * 100) / 100
const round4 = (n: number) => Math.round(n * 1e4) / 1e4
const round6 = (n: number) => Math.round(n * 1e6) / 1e6

// Wraps <TxForm> in the standard side-drawer. The single render site lives at
// the app root so any screen — /transactions list, /cashflow drill, command
// palette — opens the same form just by setting app-state's `drawerTx`.
export function TransactionFormDrawer({
  drawerTx,
  onClose,
}: {
  drawerTx: DrawerTx
  onClose: () => void
}) {
  const defaults =
    drawerTx.mode === 'new'
      ? { accountId: drawerTx.defaults?.accountId ?? null, organizationId: drawerTx.defaults?.organizationId ?? null }
      : { accountId: null, organizationId: null }
  return (
    <Drawer
      title={drawerTx.mode === 'new' ? 'Нова операція' : 'Редагування операції'}
      onClose={onClose}
      width={520}
    >
      <TxForm
        mode={drawerTx.mode}
        initial={drawerTx.mode === 'edit' ? drawerTx.transaction : null}
        defaults={defaults}
        onClose={onClose}
      />
    </Drawer>
  )
}

export function TxForm({
  mode,
  initial,
  defaults,
  onClose,
}: {
  mode: 'new' | 'edit'
  initial: Transaction | null
  defaults: { accountId: string | null; organizationId: string | null }
  onClose: () => void
}) {
  const me = useMe()
  const isAdmin = me.data?.role === 'admin'

  const organizations = useOrganizations()
  const accounts = useAccounts()
  const categories = useCategories()
  const counterparties = useCounterparties()
  const employees = useEmployees()
  const projects = useProjects()
  const tags = useTags()

  const create = useCreateTransaction()
  const update = useUpdateTransaction()
  const addSalaryPayment = useAddSalaryPayment()

  const [organizationId, setOrganizationId] = useState(
    initial?.organizationId ?? defaults.organizationId ?? '',
  )
  const [type, setType] = useState<TransactionType>(initial?.type ?? 'expense')
  const [date, setDate] = useState<string>(
    initial?.date ?? new Date().toISOString().slice(0, 10),
  )
  const [accountId, setAccountId] = useState(
    initial?.accountId ?? defaults.accountId ?? '',
  )
  const [toAccountId, setToAccountId] = useState(initial?.toAccountId ?? '')
  const [toAmount, setToAmount] = useState(initial?.toAmount ? String(initial.toAmount) : '')
  const [counterpartyId, setCounterpartyId] = useState(initial?.counterpartyId ?? '')
  // Salary-mode picks (meaningful when the «Зарплата» category is chosen). The
  // accrual is the salary_entries row the payout settles. In edit mode they're
  // prefilled from the op's existing salary-ledger link (if any).
  const [employeeId, setEmployeeId] = useState(initial?.salaryEmployeeId ?? '')
  const [accrualId, setAccrualId] = useState(initial?.salaryEntryId ?? '')
  const [categoryId, setCategoryId] = useState(initial?.categoryId ?? '')
  const [projectId, setProjectId] = useState(initial?.projectId ?? '')
  const [amount, setAmount] = useState(String(initial?.amount ?? ''))
  const [currency, setCurrency] = useState<Currency>(initial?.currency ?? 'UAH')
  const [fxRate, setFxRate] = useState(initial?.fxRate ? String(initial.fxRate) : '')
  // Displayed conversion rate. For a UAH-involved conversion we quote it the
  // conventional way (UAH per 1 unit of the foreign currency, e.g. 1 EUR = 51 ₴);
  // for a rare foreign↔foreign move we keep the raw destination-per-source ratio.
  // Derived from the stored amounts, so existing transfers always show the
  // intuitive direction even though the DB only persists amount + toAmount.
  const [convRate, setConvRate] = useState(() => {
    if (!initial?.toAmount || !initial?.amount) return ''
    const src = initial.currency
    const dst = initial.toCurrency
    if (src !== 'UAH' && dst !== 'UAH') {
      return String(round6(initial.toAmount / initial.amount))
    }
    const uahPerForeign =
      src === 'UAH' ? initial.amount / initial.toAmount : initial.toAmount / initial.amount
    return String(round4(uahPerForeign))
  })
  // The rate is auto-pulled from saved fx_rates for a new conversion. Once the
  // user edits the rate or the received amount, we stop overwriting it; editing
  // an existing op never auto-overwrites the stored-derived rate.
  const [rateTouched, setRateTouched] = useState(mode === 'edit')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [tagIds, setTagIds] = useState<string[]>(initial?.tagIds ?? [])
  // The combobox triggers are <button>s, so the browser's native `required`
  // validation no longer fires — guard the mandatory picks here instead.
  const [formError, setFormError] = useState<string | null>(null)

  // Every field is editable by everyone, including synced ops (finmap/api/webhook)
  // — corrections must not require an admin. A synced op still shows a "next sync
  // may overwrite" warning below so the edit's fragility stays visible.
  const synced = !!initial && initial.source !== 'manual' && initial.source !== 'plan'
  const editable = true

  const accountForFx = accounts.data?.find((a) => a.id === accountId)
  const needsAccountFx = !!accountForFx && accountForFx.currency !== currency
  const accountQuote = needsAccountFx
    ? accountFxQuote(currency, accountForFx.currency)
    : null
  const accountAmount =
    needsAccountFx && Number(amount) > 0 && Number(fxRate) > 0
      ? amountInAccountCurrency({
          amount: Number(amount),
          transactionCurrency: currency,
          accountCurrency: accountForFx.currency,
          fxRate: Number(fxRate),
        })
      : null
  const filteredCats = (categories.data ?? []).filter((c) => c.kind === type)

  // Salary mode: picking the «Зарплата» expense category turns the form into a
  // salary payout — «Контрагент» becomes an employee + accrual picker. A NEW op
  // is recorded via POST /salaries/:id/payments; editing an existing salary op
  // PATCHes the tx with the accrual link, keeping the ledger in sync (it shows in
  // «Виплачено» and settles the debt).
  const isSalary = type === 'expense' && categoryId === SALARY_CATEGORY_ID

  const accrualsQ = useSalaryEntries({ employeeId }, { enabled: isSalary && !!employeeId })
  // Only accruals that still owe money can receive a NEW payout. Newest first.
  const openAccruals = useMemo(
    () =>
      (accrualsQ.data?.items ?? [])
        .filter((e) => e.debt > 0)
        .sort((a, b) => b.periodYear - a.periodYear || b.periodMonth - a.periodMonth),
    [accrualsQ.data],
  )
  // When editing a payout, its own accrual may already be fully paid (debt 0, so
  // absent from openAccruals) — keep it selectable by unioning it back in.
  const linkedAccrual = useMemo(
    () =>
      mode === 'edit' && initial?.salaryEntryId
        ? (accrualsQ.data?.items ?? []).find((e) => e.id === initial.salaryEntryId) ?? null
        : null,
    [mode, initial?.salaryEntryId, accrualsQ.data],
  )
  const selectableAccruals = useMemo(
    () =>
      linkedAccrual && !openAccruals.some((e) => e.id === linkedAccrual.id)
        ? [linkedAccrual, ...openAccruals]
        : openAccruals,
    [linkedAccrual, openAccruals],
  )
  const selectedAccrual = selectableAccruals.find((e) => e.id === accrualId) ?? null

  // Employees aren't org-bound (they live in the paymaster org), so the picker
  // lists everyone — active first, then by name. A payout can come from any org's
  // account, exactly like the «Провести виплату» flow on the Salaries screen.
  const employeeOptions = useMemo(
    () =>
      (employees.data?.items ?? [])
        .slice()
        .sort(
          (a, b) =>
            Number(b.isActive) - Number(a.isActive) ||
            a.fullName.localeCompare(b.fullName, 'uk'),
        ),
    [employees.data],
  )

  // Default the accrual to the one matching the operation's month (else the newest
  // open one) and prefill the amount with its outstanding balance. Only fires when
  // the current pick isn't a valid open accrual, so it never fights a manual edit.
  const opYear = Number(date.slice(0, 4))
  const opMonth = Number(date.slice(5, 7))
  useEffect(() => {
    if (!isSalary || !employeeId) return
    if (selectableAccruals.some((e) => e.id === accrualId)) return
    if (selectableAccruals.length === 0) {
      if (accrualId) setAccrualId('')
      return
    }
    const match = openAccruals.find((e) => e.periodYear === opYear && e.periodMonth === opMonth)
    const next = match ?? selectableAccruals[0]
    setAccrualId(next.id)
    // Prefill the amount with the outstanding balance only for a NEW payout —
    // editing must keep the operation's own amount.
    if (mode === 'new') setAmount(next.debt > 0 ? String(next.debt) : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSalary, employeeId, selectableAccruals])

  const orgNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const o of organizations.data ?? []) map.set(o.id, o.name)
    return map
  }, [organizations.data])

  // The source account must belong to the selected organization. Sorted newest
  // first (createdAt desc, then id desc) so the auto-selected default is the most
  // recently created account.
  const orgAccounts = useMemo(
    () =>
      (accounts.data ?? [])
        .filter((a) => a.organizationId === organizationId && !a.isArchived)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt) || b.id.localeCompare(a.id)),
    [accounts.data, organizationId],
  )

  // Only organizations that have at least one non-archived account are worth
  // picking (you can't book an operation without an account). The currently
  // selected org is always kept in the list so the value stays valid.
  const orgOptions = useMemo(() => {
    const withAccounts = new Set(
      (accounts.data ?? []).filter((a) => !a.isArchived).map((a) => a.organizationId),
    )
    return (organizations.data ?? []).filter(
      (o) => withAccounts.has(o.id) || o.id === organizationId,
    )
  }, [organizations.data, accounts.data, organizationId])

  // New operation: default to the first organization that actually has accounts
  // (the parent's default may be an account-less org).
  useEffect(() => {
    if (mode !== 'new') return
    const withAccounts = new Set(
      (accounts.data ?? []).filter((a) => !a.isArchived).map((a) => a.organizationId),
    )
    if (organizationId && withAccounts.has(organizationId)) return
    const firstWithAccounts = (organizations.data ?? []).find((o) => withAccounts.has(o.id))
    if (firstWithAccounts) setOrganizationId(firstWithAccounts.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations.data, accounts.data])

  // When the org changes (or on first load), keep the source account valid for
  // that org: if the current pick isn't one of the org's accounts, fall back to
  // the newest one and adopt its currency.
  useEffect(() => {
    if (!editable) return
    if (orgAccounts.some((a) => a.id === accountId)) return
    const next = orgAccounts[0]
    setAccountId(next?.id ?? '')
    if (next) setCurrency(next.currency)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, orgAccounts])

  // A new op prefilled from the rail filter inherits that account — and must
  // inherit its currency too. The currency state starts at UAH, and the effect
  // above only adopts a currency when it *reassigns* the account, so a still-valid
  // EUR/USD default would otherwise leave the amount on UAH. Apply the default
  // account's currency once it resolves; a manual currency pick wins afterwards.
  const defaultCurrencyApplied = useRef(mode !== 'new' || !defaults.accountId)
  useEffect(() => {
    if (defaultCurrencyApplied.current) return
    const acc = accounts.data?.find((a) => a.id === accountId)
    if (!acc) return
    setCurrency(acc.currency)
    defaultCurrencyApplied.current = true
  }, [accounts.data, accountId])

  const toAccount = accounts.data?.find((a) => a.id === toAccountId)
  const dstCurrency = toAccount?.currency
  // Cross-currency transfer: dest leg lands in a different currency, so the caller
  // states how much actually arrived (to_amount). Same-currency transfers skip this.
  const isConversion = type === 'transfer' && !!toAccount && dstCurrency !== currency
  // The common case is UAH on one leg and USD/EUR on the other — there we quote and
  // auto-pull the rate the conventional way (UAH per 1 foreign unit). A foreign↔foreign
  // move has no single fx_rates quote, so it falls back to manual destination-per-source.
  const uahInvolved = isConversion && (currency === 'UAH' || dstCurrency === 'UAH')
  const uahIsSource = currency === 'UAH'
  const foreignCurrency: 'USD' | 'EUR' | null = uahInvolved
    ? ((uahIsSource ? dstCurrency : currency) as 'USD' | 'EUR')
    : null

  // toAmount ↔ displayed-rate conversions. For a UAH leg the rate is UAH-per-foreign,
  // so the foreign side is divided (UAH→foreign) or multiplied (foreign→UAH); the
  // foreign↔foreign fallback keeps the literal destination-per-source ratio.
  const toAmountFromRate = (a: number, rate: number) =>
    !uahInvolved ? a * rate : uahIsSource ? a / rate : a * rate
  const rateFromToAmount = (a: number, t: number) =>
    !uahInvolved ? t / a : uahIsSource ? a / t : t / a
  const roundRate = uahInvolved ? round4 : round6

  // Auto-pull the saved rate for a brand-new UAH↔foreign conversion, until the
  // user takes over. The spread side follows the direction: UAH→foreign buys
  // currency (bank sells at sellRate), foreign→UAH sells it (bank buys at buyRate).
  const autoFx = useLatestFxRate(foreignCurrency ?? 'USD', date, {
    enabled: mode === 'new' && uahInvolved,
  })
  useEffect(() => {
    if (mode !== 'new' || !uahInvolved || rateTouched) return
    const r = uahIsSource ? autoFx.data?.sellRate : autoFx.data?.buyRate
    if (!r || !Number.isFinite(r)) return
    setConvRate(String(round4(r)))
    const a = Number(amount)
    if (a > 0) setToAmount(String(round2(toAmountFromRate(a, r))))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, uahInvolved, uahIsSource, foreignCurrency, rateTouched, autoFx.data])

  // Keep amount / received-amount / rate consistent. Editing the rate or the
  // received amount writes back the other and marks the rate user-owned (stops
  // auto-pull); changing the source amount re-derives the received amount from
  // the current rate. round2 for money, round4/round6 for the rate.
  const onAmountChange = (v: string) => {
    setAmount(v)
    const a = Number(v)
    const r = Number(convRate)
    if (isConversion && convRate && a > 0 && r > 0)
      setToAmount(String(round2(toAmountFromRate(a, r))))
  }
  const onToAmountChange = (v: string) => {
    setToAmount(v)
    setRateTouched(true)
    const a = Number(amount)
    const t = Number(v)
    setConvRate(v && a > 0 && t > 0 ? String(roundRate(rateFromToAmount(a, t))) : '')
  }
  const onConvRateChange = (v: string) => {
    setConvRate(v)
    setRateTouched(true)
    const a = Number(amount)
    const r = Number(v)
    if (v && a > 0 && r > 0) setToAmount(String(round2(toAmountFromRate(a, r))))
  }

  const submit = async () => {
    if (!editable) return
    setFormError(null)
    if (!organizationId || !accountId || !categoryId) {
      setFormError('Заповніть обовʼязкові поля: організація, рахунок, категорія')
      return
    }
    if (needsAccountFx && !(Number(fxRate) > 0)) {
      setFormError(`Вкажіть курс для зарахування на рахунок у ${accountForFx.currency}`)
      return
    }
    // Salary payout: keep the salary ledger in sync rather than booking a plain,
    // unlinked expense. NEW → POST /salaries/:id/payments (creates tx + link
    // atomically). EDIT → PATCH the tx with the accrual link (re-link, or unlink
    // when no accrual is chosen); the backend mirrors amount/date into the link.
    if (isSalary) {
      const amt = Number(amount)
      if (!(amt > 0)) {
        setFormError('Вкажіть суму виплати')
        return
      }
      if (accrualId && selectedAccrual && currency !== selectedAccrual.currency) {
        setFormError(
          `Валюта рахунку (${currency}) має збігатися з валютою нарахування (${selectedAccrual.currency})`,
        )
        return
      }
      if (mode === 'new') {
        if (!employeeId) {
          setFormError('Оберіть співробітника')
          return
        }
        if (!accrualId || !selectedAccrual) {
          setFormError('Оберіть нарахування (або створіть його на екрані «Зарплати»)')
          return
        }
        await addSalaryPayment.mutateAsync({
          id: accrualId,
          accountId,
          amount: amt,
          paymentDate: date,
        })
      } else if (initial) {
        await update.mutateAsync({
          id: initial.id,
          patch: {
            organizationId,
            date,
            accountId,
            categoryId,
            amount: amt,
            currency,
            description: description.trim() || null,
            // '' → unlink (plain salary expense); an id (re)links/creates the payout.
            salaryEntryId: accrualId || null,
          },
        })
      }
      onClose()
      return
    }
    if (type === 'transfer' && !toAccountId) {
      setFormError('Оберіть рахунок-отримувач')
      return
    }
    const payload: CreateTransactionInput = {
      organizationId,
      type,
      date,
      accountId,
      toAccountId: type === 'transfer' ? toAccountId || null : null,
      counterpartyId: counterpartyId || null,
      categoryId,
      projectId: projectId || null,
      amount: Number(amount),
      currency,
      toAmount: isConversion ? Number(toAmount) : null,
      fxRate: fxRate ? Number(fxRate) : null,
      description: description.trim() || null,
      tagIds,
    }
    if (mode === 'new') {
      await create.mutateAsync(payload)
    } else if (initial) {
      // Type is fixed after creation; the organization (and the rest) is editable.
      const { type: _omitType, ...patch } = payload
      void _omitType
      await update.mutateAsync({ id: initial.id, patch })
    }
    onClose()
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        void submit()
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      {synced && (
        <div style={{ fontSize: 12, color: 'var(--warning-fg)' }}>
          Синхронізована операція (джерело «{initial?.source}»). Зміни може перезаписати наступна
          синхронізація.
        </div>
      )}

      {mode === 'edit' && initial && <TxMeta tx={initial} />}

      <FieldRow>
        <Field label="Організація *">
          <SearchSelect
            style={inputStyle}
            value={organizationId}
            onChange={setOrganizationId}
            disabled={!editable}
            placeholder="Оберіть організацію…"
            options={orgOptions.map((o) => ({ id: o.id, label: o.name }))}
          />
        </Field>
        <Field label="Тип *">
          <select
            style={inputStyle}
            value={type}
            onChange={(e) => setType(e.target.value as TransactionType)}
            disabled={mode === 'edit' || !editable}
          >
            <option value="income">Надходження</option>
            <option value="expense">Витрата</option>
            <option value="transfer">Переказ</option>
          </select>
        </Field>
      </FieldRow>

      <FieldRow>
        <Field label="Дата *">
          <DatePicker
            style={inputStyle}
            value={date}
            onChange={setDate}
            disabled={!editable}
          />
        </Field>
        <Field label="Рахунок *">
          <SearchSelect
            style={inputStyle}
            value={accountId}
            onChange={(id) => {
              setAccountId(id)
              const acc = accounts.data?.find((a) => a.id === id)
              if (acc) setCurrency(acc.currency)
            }}
            disabled={!editable}
            placeholder="Оберіть рахунок…"
            options={orgAccounts.map((a) => ({ id: a.id, label: a.name, hint: a.currency }))}
          />
        </Field>
      </FieldRow>

      {type === 'transfer' && (
        <Field label="Рахунок-отримувач *">
          <SearchSelect
            style={inputStyle}
            value={toAccountId}
            onChange={setToAccountId}
            disabled={!editable}
            placeholder="Оберіть рахунок…"
            options={(accounts.data ?? [])
              .filter((a) => a.id !== accountId)
              .map((a) => {
                const orgName = orgNameById.get(a.organizationId)
                const crossOrg = a.organizationId !== organizationId
                return {
                  id: a.id,
                  label: a.name,
                  hint: crossOrg && orgName ? `${a.currency} · ${orgName}` : a.currency,
                }
              })}
          />
        </Field>
      )}

      <Field label="Категорія *">
        <SearchSelect
          style={inputStyle}
          value={categoryId}
          onChange={setCategoryId}
          disabled={!editable}
          placeholder="Оберіть категорію…"
          options={filteredCats.map((c) => ({
            id: c.id,
            label: c.name,
            hint: c.isSystem ? 'system' : undefined,
          }))}
        />
      </Field>

      <FieldRow>
        <Field label="Сума *">
          <AmountInput
            style={inputStyle}
            decimals={2}
            required
            value={amount}
            onChange={onAmountChange}
            disabled={!editable}
          />
        </Field>
        <Field label="Валюта *">
          <select
            style={inputStyle}
            value={currency}
            onChange={(e) => setCurrency(e.target.value as Currency)}
            disabled={!editable}
          >
            <option value="UAH">UAH</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
          </select>
        </Field>
      </FieldRow>

      {isConversion && (
        <FieldRow>
          <Field
            label={
              uahInvolved
                ? `Курс (1 ${foreignCurrency} = ? грн)`
                : `Курс (1 ${currency} = ? ${dstCurrency})`
            }
          >
            <AmountInput
              style={inputStyle}
              decimals={4}
              value={convRate}
              onChange={onConvRateChange}
              disabled={!editable}
              placeholder={uahInvolved ? 'напр. 51.3' : 'напр. 1.08'}
            />
          </Field>
          <Field label={`Сума зарахована (${dstCurrency}) *`}>
            <AmountInput
              style={inputStyle}
              decimals={2}
              required
              value={toAmount}
              onChange={onToAmountChange}
              disabled={!editable}
              placeholder={`Скільки надійшло у ${dstCurrency}`}
            />
          </Field>
        </FieldRow>
      )}

      {needsAccountFx && accountQuote && (
        <Field label={`Курс (1 ${accountQuote.base} = ? ${accountQuote.quote}) *`}>
          <AmountInput
            style={inputStyle}
            decimals={4}
            required
            value={fxRate}
            onChange={setFxRate}
            disabled={!editable}
            placeholder={accountQuote.quote === 'UAH' ? 'напр. 41.5' : 'напр. 1.13'}
          />
          {accountAmount !== null && (
            <div style={{ marginTop: 4, fontSize: 12, color: 'var(--fg-3)' }}>
              На рахунок буде зараховано: {fmtMoney(accountAmount, accountForFx.currency)}
            </div>
          )}
        </Field>
      )}

      {isSalary ? (
        <>
          <Field label="Співробітник *">
            <SearchSelect
              style={inputStyle}
              value={employeeId}
              onChange={(id) => {
                setEmployeeId(id)
                setAccrualId('')
              }}
              disabled={!editable}
              placeholder="Оберіть співробітника…"
              options={employeeOptions.map((e) => ({
                id: e.id,
                label: e.fullName,
                hint: e.isActive ? undefined : 'неактивний',
              }))}
            />
          </Field>

          {employeeId &&
            (accrualsQ.isLoading ? (
              <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>Завантаження нарахувань…</div>
            ) : selectableAccruals.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--warning-fg)' }}>
                У співробітника немає відкритого нарахування. Спочатку створіть нарахування за
                період на екрані «Зарплати».
              </div>
            ) : (
              <Field label="Період (нарахування) *">
                <SearchSelect
                  style={inputStyle}
                  value={accrualId}
                  onChange={(id) => {
                    setAccrualId(id)
                    // Prefill amount from the outstanding balance only for a NEW
                    // payout; editing keeps the operation's own amount.
                    const e = selectableAccruals.find((x) => x.id === id)
                    if (e && mode === 'new') setAmount(e.debt > 0 ? String(e.debt) : '')
                  }}
                  disabled={!editable}
                  placeholder="Оберіть нарахування…"
                  options={selectableAccruals.map((e) => ({
                    id: e.id,
                    label: `${e.periodYear}-${String(e.periodMonth).padStart(2, '0')}`,
                    hint:
                      e.debt > 0
                        ? `залишок ${fmtMoney(e.debt, e.currency)}`
                        : `виплачено ${fmtMoney(e.net, e.currency)}`,
                  }))}
                />
              </Field>
            ))}

          {selectedAccrual && currency !== selectedAccrual.currency && (
            <div style={{ fontSize: 12, color: 'var(--warning-fg)' }}>
              Валюта рахунку ({currency}) має збігатися з валютою нарахування (
              {selectedAccrual.currency}). Оберіть рахунок у {selectedAccrual.currency}.
            </div>
          )}
        </>
      ) : (
        <>
          <Field label="Контрагент">
            <SearchSelect
              style={inputStyle}
              value={counterpartyId}
              onChange={setCounterpartyId}
              disabled={!editable}
              emptyOption
              options={(counterparties.data ?? []).map((cp) => ({ id: cp.id, label: cp.name }))}
            />
          </Field>

          <Field label="Проєкт">
            <SearchSelect
              style={inputStyle}
              value={projectId}
              onChange={setProjectId}
              disabled={!editable}
              emptyOption
              options={(projects.data ?? []).map((p) => ({ id: p.id, label: p.name }))}
            />
          </Field>

          <Field label="Опис">
            <input
              style={inputStyle}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!editable}
            />
          </Field>

          <Field label="Теги">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(tags.data ?? []).map((t) => {
                const active = tagIds.includes(t.id)
                return (
                  <button
                    key={t.id}
                    type="button"
                    disabled={!editable}
                    onClick={() => {
                      setTagIds((prev) =>
                        prev.includes(t.id) ? prev.filter((x) => x !== t.id) : [...prev, t.id],
                      )
                    }}
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 999,
                      padding: '4px 10px',
                      background: active ? 'var(--accent-3)' : 'transparent',
                      color: active ? 'var(--accent-12)' : 'var(--fg-2)',
                      fontSize: 12,
                      cursor: editable ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {t.name}
                  </button>
                )
              })}
            </div>
          </Field>
        </>
      )}

      {editable && (
        <Button
          variant="primary"
          type="submit"
          disabled={create.isPending || update.isPending || addSalaryPayment.isPending}
        >
          {create.isPending || update.isPending || addSalaryPayment.isPending
            ? 'Збереження…'
            : 'Зберегти'}
        </Button>
      )}

      {formError && <div style={{ color: 'var(--danger-9)', fontSize: 12 }}>{formError}</div>}

      {(create.error || update.error || addSalaryPayment.error) && (
        <div style={{ color: 'var(--danger-9)', fontSize: 12 }}>
          {(create.error || update.error || addSalaryPayment.error) instanceof Error
            ? ((create.error || update.error || addSalaryPayment.error) as Error).message
            : 'Помилка збереження'}
        </div>
      )}

      {!isAdmin && mode === 'edit' && (
        <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>
          Видалення доступне лише адміністраторам.
        </div>
      )}
    </form>
  )
}

// Read-only identity block in the detail card: the transaction id (copyable —
// needed to reference a row for manual cleanup) plus its author, source and
// timestamps. Author is shown as the raw user id (no users API to resolve it).
function TxMeta({ tx }: { tx: Transaction }) {
  const [copied, setCopied] = useState(false)
  const copyId = () => {
    void navigator.clipboard?.writeText(tx.id).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }
  const mono: React.CSSProperties = {
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    color: 'var(--fg-2)',
    wordBreak: 'break-all',
  }
  const label: React.CSSProperties = { fontSize: 10, color: 'var(--fg-3)', letterSpacing: '0.04em' }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '8px 10px',
        background: 'var(--bg-3)',
        borderRadius: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={label}>ID ОПЕРАЦІЇ</div>
          <div style={mono}>{tx.id}</div>
        </div>
        <Button variant="ghost" size="sm" icon={copied ? 'check' : 'copy'} onClick={copyId}>
          {copied ? 'Скопійовано' : 'Копіювати'}
        </Button>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={label}>АВТОР (ID)</div>
          <div style={mono}>{tx.createdBy}</div>
        </div>
        <div>
          <div style={label}>ДЖЕРЕЛО</div>
          <div style={mono}>{tx.source}</div>
        </div>
        <div>
          <div style={label}>СТВОРЕНО · ОНОВЛЕНО</div>
          <div style={mono}>
            {tx.createdAt}
            {tx.updatedAt && tx.updatedAt !== tx.createdAt ? ` · ${tx.updatedAt}` : ''}
          </div>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontSize: 12,
        color: 'var(--fg-3)',
        flex: 1,
      }}
    >
      <span>{label}</span>
      {children}
    </label>
  )
}

function FieldRow({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', gap: 8 }}>{children}</div>
}
